import { ChevronDownIcon, ReplyIcon } from "@/components/icons";
import { Avatar } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { Note, Site } from "../api.ts";
import {
  authorView,
  pageGroups,
  pagePath,
  type AuthorView,
  type PageFilter,
} from "../note-view.ts";
import { exactTime, shortRelativeTime } from "../relative-time.ts";

export type NoteStateFilter = "open" | "resolved";

/** The author's disc: member colour, agent glyph, or the neutral guest. */
export function AuthorAvatar({
  author,
  size = "sm",
}: {
  readonly author: AuthorView;
  readonly size?: "sm" | "default";
}) {
  return <Avatar person={author.person} size={size} />;
}

/** "Agent" or "Guest" after a name; members carry no word. */
export function AuthorKind({ author }: { readonly author: AuthorView }) {
  return author.kind === "member" ? null : (
    <span className="note-kind" title={author.via ?? undefined}>
      {author.kind === "agent" ? "Agent" : "Guest"}
    </span>
  );
}

/** One note in the list: body, then who and where; replies and age right. */
export function NoteListRow({
  note,
  onOpen,
}: {
  readonly note: Note;
  readonly onOpen: () => void;
}) {
  const author = authorView(note);
  const replies = note.replies.length;
  return (
    <li>
      <button
        className="note-row"
        data-note-id={note.id}
        onClick={onOpen}
        type="button"
      >
        <span className="note-row-main">
          <span className="note-row-body">{note.body}</span>
          <span className="note-row-meta">
            {author.name}
            <span aria-hidden="true"> · </span>
            <span className="sr-only">, on </span>
            {note.site_name ?? "Site"} {pagePath(note.page_url)}
          </span>
        </span>
        <span className="note-row-side">
          <span className="note-row-replies">
            {replies === 0 ? null : (
              <>
                <ReplyIcon />
                {replies}
                <span className="sr-only">
                  {replies === 1 ? " reply" : " replies"}
                </span>
              </>
            )}
          </span>
          <time
            className="note-row-time"
            dateTime={note.updated_at}
            title={exactTime(note.updated_at)}
          >
            {shortRelativeTime(note.updated_at)}
          </time>
        </span>
      </button>
    </li>
  );
}

/** Open and Resolved as text tabs, with counts when they are known. */
export function NoteStateTabs({
  counts,
  filter,
  onFilter,
}: {
  readonly counts: {
    readonly open: number;
    readonly resolved: number;
  } | null;
  readonly filter: NoteStateFilter;
  readonly onFilter: (filter: NoteStateFilter) => void;
}) {
  return (
    <div aria-label="Note state" className="note-tabs" role="group">
      {(
        [
          ["open", "Open"],
          ["resolved", "Resolved"],
        ] as const
      ).map(([value, label]) => (
        <button
          aria-pressed={filter === value}
          key={value}
          onClick={() => onFilter(value)}
          type="button"
        >
          {label}
          {counts === null ? null : <b>{counts[value]}</b>}
        </button>
      ))}
    </div>
  );
}

/**
 * The quiet site menu. Choosing a site keeps the menu open and lists that
 * site's pages under it, so a page is one more click.
 */
export function SiteMenu({
  notes,
  onPage,
  onSite,
  page,
  siteId,
  sites,
}: {
  readonly notes: readonly Note[];
  readonly onPage: (page: PageFilter | null) => void;
  readonly onSite: (siteId: string | null) => void;
  readonly page: PageFilter | null;
  readonly siteId: string | null;
  readonly sites: readonly Site[];
}) {
  const site = sites.find((candidate) => candidate.id === siteId);
  const pages =
    siteId === null
      ? []
      : (pageGroups(notes).find((group) => group.siteId === siteId)?.paths ??
        []);
  const value =
    page !== null
      ? `page:${page.siteId} ${page.path}`
      : siteId !== null
        ? `site:${siteId}`
        : "all";
  const label =
    siteId === null
      ? "All sites"
      : `${site?.name ?? "Site"}${page === null ? "" : ` ${page.path}`}`;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`Site: ${label}`}
        className="quiet-menu-button"
      >
        <span className="truncate">{label}</span>
        <ChevronDownIcon />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[280px]">
        <DropdownMenuRadioGroup
          value={value}
          onValueChange={(next) => {
            if (typeof next !== "string" || next === "all") {
              onSite(null);
              return;
            }
            if (next.startsWith("site:")) {
              onSite(next.slice(5));
              return;
            }
            const rest = next.slice(5);
            const split = rest.indexOf(" ");
            onPage({
              siteId: rest.slice(0, split),
              path: rest.slice(split + 1),
            });
          }}
        >
          <DropdownMenuRadioItem closeOnClick value="all">
            All sites
          </DropdownMenuRadioItem>
          {sites.length === 0 ? null : <DropdownMenuSeparator />}
          {sites.map((candidate) => (
            <SiteItems
              key={candidate.id}
              pages={candidate.id === siteId ? pages : []}
              site={candidate}
            />
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SiteItems({
  pages,
  site,
}: {
  readonly pages: readonly { readonly path: string; readonly count: number }[];
  readonly site: Site;
}) {
  return (
    <>
      <DropdownMenuRadioItem closeOnClick={false} value={`site:${site.id}`}>
        <span className="min-w-0 flex-1 truncate">{site.name}</span>
        {site.enabled ? null : (
          <span className="text-xs text-fg-3">Archived</span>
        )}
      </DropdownMenuRadioItem>
      {pages.map((entry) => (
        <DropdownMenuRadioItem
          closeOnClick
          inset
          key={entry.path}
          value={`page:${site.id} ${entry.path}`}
        >
          <span className="min-w-0 flex-1 truncate">{entry.path}</span>
          <span className="text-xs text-fg-3 tabular-nums">{entry.count}</span>
        </DropdownMenuRadioItem>
      ))}
    </>
  );
}
